import React, { useState, useContext, PropsWithChildren } from 'react';
import BookmarksOutlinedIcon from '@mui/icons-material/BookmarksOutlined';
import CloseIcon from '@mui/icons-material/Close';
import MuiExitToApp from '@mui/icons-material/ExitToApp';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import MuiSettings from '@mui/icons-material/Settings';
import Box from '@mui/material/Box';
import Fab from '@mui/material/Fab';
import Grid from '@mui/material/Grid';
import { i18n_meditations_goal } from '@yi/i18n/phrases/i18n_meditations_goal';
import { i18n_my_account } from '@yi/i18n/phrases/i18n_my_account';
import { i18n_my_certificates } from '@yi/i18n/phrases/i18n_my_certificates';
import { i18n_my_library } from '@yi/i18n/phrases/i18n_my_library';
import { i18n_my_practice } from '@yi/i18n/phrases/i18n_my_practice';
import { i18n_my_premium_trainings } from '@yi/i18n/phrases/i18n_my_premium_trainings';
import { i18n_see_all_stats } from '@yi/i18n/phrases/i18n_see_all_stats';
import { i18n_sign_in } from '@yi/i18n/phrases/i18n_sign_in';
import { i18n_sign_out } from '@yi/i18n/phrases/i18n_sign_out';
import { i18n_start_your_free_trial_caps } from '@yi/i18n/phrases/i18n_start_your_free_trial_caps';
import { i18n_yoga_class_goal } from '@yi/i18n/phrases/i18n_yoga_class_goal';

import { analytics, utilities } from '~/common';
import { logUserOut } from '~/src/common/logUserOut';
import { LIBRARY_PATHNAME, LOGGED_OUT_HOME_PATHNAME } from '~/src/common/routes';
import { useCurrentUser } from '~/src/common/user';
import { darkGrey, pinkishOrange, turquoise } from '~/styles';

import { DropDownMenuContainer, MenuItem, MenuLink, MenuExternalLink } from './elements';
import { ButtonLocalizedLink, Button } from '../../buttons';
import { CurrentUserAvatar } from '../../CurrentUserAvatar';
import { IconPremiumTrainings } from '../../icons';
import { useLocale } from '../../LocaleProvider';
import { LocalizedLink } from '../../LocalizedLink';
import { SignInModal } from '../../modals';
import { PlaylistContext } from '../../PlayListPlayer';
import { TrackerCard } from '../../TrackerCard';

interface Props {
  showUserMenu: boolean;
  toggleUserMenu(): void;
}

const sendLogoutButtonClickEvent = () => {
  analytics.logAnalyticsEvent({
    event: 'logoutButtonClick',
    eventCategory: 'Account',
    eventAction: 'logoutButtonClick',
    eventLabel: 'logoutButtonClick',
    eventNonInteraction: true,
    category: 'Account',
    label: 'logoutButtonClick',
  });
};

export const UserMenu: React.FC<PropsWithChildren<Props>> = ({ showUserMenu, toggleUserMenu }) => {
  const { currentUser, isLoggedIn } = useCurrentUser();
  const { locale } = useLocale();
  const { playlistEnabled, showDetailView } = useContext(PlaylistContext);
  const [showSignInModal, setShowSignInModal] = useState(false);
  const [hideUserMenu, setHideUserMenu] = useState(false);
  const { getLocalizedDefaultSignupPath } = utilities;

  if (!showUserMenu) return null;
  return (
    <DropDownMenuContainer
      isLoggedIn={isLoggedIn}
      playlistEnabled={playlistEnabled}
      playlistDetailView={showDetailView}
      hideUserMenu={hideUserMenu}
    >
      {isLoggedIn ? (
        <>
          <Grid
            container
            justifyContent="center"
            alignItems="center"
            style={{ flexDirection: 'column' }}
          >
            {!!currentUser && (
              <LocalizedLink to="/stats" style={{ textDecoration: 'none' }}>
                <Box
                  textAlign="center"
                  bgcolor="#fff"
                  borderRadius="50%"
                  style={{ transform: 'translateY(-25%)' }}
                >
                  <CurrentUserAvatar fontSize="50px" width={90} />
                </Box>
              </LocalizedLink>
            )}
            <div>
              {!!currentUser && (
                <>
                  <Grid container justifyContent="center" alignItems="center">
                    <LocalizedLink to="/stats" style={{ textDecoration: 'none' }}>
                      <Box
                        component="h6"
                        fontSize={20.3}
                        textAlign="center"
                        fontWeight={200}
                        color="white"
                      >
                        {`${currentUser.first_name || ''} ${currentUser.last_name || ''}`}{' '}
                      </Box>
                    </LocalizedLink>
                  </Grid>
                  <Box
                    component="span"
                    fontSize={15.8}
                    textAlign="center"
                    fontWeight={500}
                    color={turquoise}
                  >
                    {currentUser.email || ''}
                  </Box>
                </>
              )}
            </div>
          </Grid>

          <Box padding="16px" margin="16px 0 0 0">
            <Grid item lg={12}>
              <Box
                pl={1}
                component="h3"
                fontSize={12}
                fontWeight={500}
                color={turquoise}
                style={{ position: 'relative', zIndex: 1 }}
              >
                <LocalizedLink to="/stats">{i18n_my_practice(locale)}</LocalizedLink>
              </Box>
            </Grid>
            {!!currentUser && (
              <Grid container spacing={2}>
                <Grid item xl={6} lg={6} md={6} sm={6} xs={6}>
                  <LocalizedLink to="stats" style={{ textDecoration: 'none' }}>
                    <TrackerCard
                      text={i18n_yoga_class_goal(locale)}
                      achievedGoals={currentUser.class_count_current_week}
                      totalGoals={currentUser.class_goal}
                      barColor={pinkishOrange}
                    />
                  </LocalizedLink>
                </Grid>
                <Grid item xl={6} lg={6} md={6} sm={6} xs={6}>
                  <LocalizedLink to="stats" style={{ textDecoration: 'none' }}>
                    <TrackerCard
                      text={i18n_meditations_goal(locale)}
                      achievedGoals={currentUser.meditation_count_current_week}
                      totalGoals={currentUser.meditation_goal}
                      barColor={turquoise}
                    />
                  </LocalizedLink>
                </Grid>
              </Grid>
            )}
            <Box mt={2}>
              <Grid container justifyContent="center">
                <ButtonLocalizedLink
                  onClick={() => analytics.sendUserMenuLinked('/stats')}
                  to="/stats"
                  kind="containedTurquoise"
                >
                  {i18n_see_all_stats(locale)}
                </ButtonLocalizedLink>
              </Grid>
            </Box>
          </Box>
          <Box mb={6} mt={2}>
            <Grid container>
              <Grid item xs={6}>
                <MenuLink
                  onClick={() => analytics.sendUserMenuLinked('/account')}
                  to="/account"
                  data-testid="user-menu-settings"
                >
                  <MenuItem>
                    <MuiSettings style={{ color: '#fff' }} />
                    {i18n_my_account(locale)}
                  </MenuItem>
                </MenuLink>
              </Grid>
              <Grid item xs={6}>
                <MenuExternalLink>
                  <MenuItem
                    data-testid="sign-out-button"
                    onClick={async () => {
                      analytics.sendUserMenuLinked(LOGGED_OUT_HOME_PATHNAME);
                      toggleUserMenu();
                      sendLogoutButtonClickEvent();
                      logUserOut(locale);
                    }}
                  >
                    <MuiExitToApp style={{ color: '#fff' }} />
                    {i18n_sign_out(locale)}
                  </MenuItem>
                </MenuExternalLink>
              </Grid>
              <Grid item xs={6}>
                {' '}
                <MenuLink
                  onClick={() => analytics.sendUserMenuLinked(LIBRARY_PATHNAME)}
                  to="/library"
                >
                  <MenuItem>
                    <BookmarksOutlinedIcon style={{ color: '#fff' }} />
                    {i18n_my_library(locale)}
                  </MenuItem>
                </MenuLink>
              </Grid>

              <Grid item xs={6}>
                {' '}
                <MenuLink
                  onClick={() => analytics.sendUserMenuLinked('/library/certificates')}
                  to="/library/certificates"
                >
                  <MenuItem>
                    <SchoolOutlinedIcon style={{ height: '24px', width: '24px' }} />
                    {i18n_my_certificates(locale)}
                  </MenuItem>
                </MenuLink>
              </Grid>

              <Grid item xs={12}>
                <MenuLink
                  onClick={() => analytics.sendUserMenuLinked('/library/premium-trainings')}
                  to="/library/premium-trainings"
                >
                  <MenuItem centered>
                    <IconPremiumTrainings />
                    {i18n_my_premium_trainings(locale)}
                  </MenuItem>
                </MenuLink>
              </Grid>
            </Grid>
          </Box>

          <Box textAlign="center" color={darkGrey}>
            <Fab
              size="small"
              color="inherit"
              aria-label="close"
              style={{ background: turquoise }}
              onClick={toggleUserMenu}
            >
              <CloseIcon />
            </Fab>
          </Box>
        </>
      ) : (
        <>
          <Box mt={10} textAlign="center">
            <Button as="a" href={`${getLocalizedDefaultSignupPath(locale)}`} kind="responsive">
              {i18n_start_your_free_trial_caps(locale)}
            </Button>
          </Box>
          <Box pb={2} mt={2} textAlign="center" color="white">
            {isLoggedIn ? (
              <button
                onClick={() => {
                  toggleUserMenu();
                  sendLogoutButtonClickEvent();
                  logUserOut(locale);
                }}
              >
                {i18n_sign_out(locale)}
              </button>
            ) : (
              <>
                <button
                  data-testid="signin-open-modal"
                  onClick={() => {
                    setShowSignInModal(true);
                    setHideUserMenu(true);
                  }}
                  style={{
                    color: 'white',
                    paddingTop: '10px',
                    backgroundColor: 'transparent',
                    border: 'transparent',
                  }}
                >
                  {i18n_sign_in(locale)}
                </button>
                <SignInModal
                  open={showSignInModal}
                  setOpen={(open: boolean) => {
                    setHideUserMenu(false);
                    toggleUserMenu();
                    setShowSignInModal(open);
                  }}
                />
              </>
            )}
          </Box>
          <Box textAlign="center" color={darkGrey} pt={3}>
            <Fab
              size="small"
              color="inherit"
              aria-label="close"
              style={{ background: turquoise }}
              onClick={toggleUserMenu}
            >
              <CloseIcon />
            </Fab>
          </Box>
        </>
      )}
    </DropDownMenuContainer>
  );
};
